/**
 * Pure-function receive-time enforcement matrix for the block list.
 *
 * All decisions are pure functions of (senderUserId, blockedUserIds) so the
 * self-test can exhaustively cover every (blocked × side-channel) pair
 * without touching IndexedDB or shared singletons.
 *
 * CRITICAL invariant: `allowEnvelopeDecryption` ALWAYS returns true. Even
 * if the sender is blocked we MUST decrypt every inbound message so the
 * Double Ratchet advances. Skipping decryption would corrupt the session
 * such that an unblock later would never recover — the receiver would be
 * missing chain keys for messages that landed during the block window.
 * The block decision is applied AFTER decryption, at the render step.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BlockEnforcementPolicy.swift`.
 */

// ---------------------------------------------------------------------------
// Decision record
// ---------------------------------------------------------------------------

/** Receive-time decision for a freshly-decrypted message from a given sender. */
export interface BlockEnforcementDecision {
  /** Whether the chat-list UI should render the message body. */
  readonly renderMessage: boolean;
  /** Whether to post a local OS/browser notification. */
  readonly postNotification: boolean;
  /** Whether to emit a delivery receipt back to the sender. */
  readonly emitDeliveryReceipt: boolean;
  /** Whether to emit a read receipt back to the sender once the user opens the conversation. */
  readonly emitReadReceipt: boolean;
  /**
   * Whether to ack typing-indicator control messages from the sender
   * (i.e. update the live "typing…" footer).
   */
  readonly answerTypingIndicator: boolean;
  /** Whether to ring the device/browser for an incoming call from the sender. */
  readonly ringCall: boolean;
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

export const BlockEnforcementPolicy = {
  /**
   * Compute the receive-time decision for a freshly-decrypted message from
   * `senderUserId`. If the sender is not in `blockedUserIds`, every
   * side-channel runs normally; if they are, every side-channel is
   * suppressed.
   *
   * Pure function — no side effects, no I/O.
   */
  decide(
    senderUserId: string,
    blockedUserIds: ReadonlySet<string>,
  ): BlockEnforcementDecision {
    if (blockedUserIds.has(senderUserId)) {
      return {
        renderMessage: false,
        postNotification: false,
        emitDeliveryReceipt: false,
        emitReadReceipt: false,
        answerTypingIndicator: false,
        ringCall: false,
      };
    }
    return {
      renderMessage: true,
      postNotification: true,
      emitDeliveryReceipt: true,
      emitReadReceipt: true,
      answerTypingIndicator: true,
      ringCall: true,
    };
  },

  /**
   * Whether the receive path is allowed to decrypt the inbound envelope
   * from `senderUserId`.
   *
   * UNCONDITIONALLY returns true regardless of block state.
   *
   * The Double Ratchet MUST advance even for blocked peers so a future
   * unblock finds the session intact. Skipping decryption would corrupt
   * the session such that decryption would fail for every message sent
   * during the block window — and for every subsequent message, because
   * the chain keys would be desynced.
   *
   * This is the central "deniable blocking" guarantee:
   * allowEnvelopeDecryption === true, always.
   */
  allowEnvelopeDecryption(
    _senderUserId: string,
    _blockedUserIds: ReadonlySet<string>,
  ): true {
    return true;
  },
} as const;
